import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Divider,
  Checkbox,
  Link as MuiLink,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setQualifications } from "../../../store/setProfile";
import { useStepCompletion } from "../hooks/useStepCompletion";

export default function Qualifications({ onDirtyChange, onValidChange, onSave }: any) {
  const dispatch = useDispatch();
  const { qualifications } = useSelector(
    (state: any) => state.profile.information
  );
  const completions = useStepCompletion();
  const isStepCompleted = completions.qualifications?.isCompleted || false;
  
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    onDirtyChange?.(false);
  }, []);

  // Auto-check the checkbox if the step is already completed and user hasn't edited
  useEffect(() => {
    if (isStepCompleted && !hasUserEdited) {
      setConfirmationChecked(true);
    }
  }, [isStepCompleted, hasUserEdited]);

  const handleQualificationChange = (key: string, value: string) => {
    dispatch(setQualifications({ [key]: parseInt(value) }));
    setHasUserEdited(true);
    setConfirmationChecked(false);
    onDirtyChange?.(true);
  };

  const handleStudentQualification = (value: string) => {
    if (value === "0" || value === "0") {
      dispatch(setQualifications({ "A-level Student": -1 }));
    }
    dispatch(setQualifications({ Student: parseInt(value) }));
    setHasUserEdited(true);
    setConfirmationChecked(false);
    onDirtyChange?.(true);
  };

  // Check if all required questions are answered
  const allQuestionsAnswered = useMemo(() => {
    const requiredKeys = ["Degree", "Qualified Teacher", "Examiner", "Enhanced DBS", "Student"];
    const allRequiredAnswered = requiredKeys.every(
      (key) => qualifications?.[key] !== undefined && qualifications[key] !== -1
    );
    
    // If student is "Yes", check if A-level Student is also answered
    if (qualifications?.["Student"] > 0) {
      return allRequiredAnswered && 
             qualifications?.["A-level Student"] !== undefined && 
             qualifications["A-level Student"] !== -1;
    }
    
    return allRequiredAnswered;
  }, [qualifications]);

  const handleConfirmationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationChecked(event.target.checked);
    onDirtyChange?.(true);
  };

  useEffect(() => {
    // Notify parent of validation status
    const isValid = allQuestionsAnswered && confirmationChecked;
    onValidChange?.(isValid);
  }, [allQuestionsAnswered, confirmationChecked, onValidChange]);

  (Qualifications as any).save = async () => {
    setSubmitted(true);
    onDirtyChange?.(false);
  };

  const qualificationQuestions = [
    {
      key: "Degree",
      label: "Do you have a degree?",
    },
    {
      key: "Qualified Teacher",
      label: "Are you a qualified teacher (e.g. teaching certificate)?",
    },
    {
      key: "Examiner",
      label: "Have you been an examiner in the last five years?",
    },
    {
      key: "Enhanced DBS",
      label: "Do you have an enhanced DBS? (not a basic DBS)",
    },
  ];

  return (
    <Box>
      <Typography 
        variant="h2"
        sx={{ 
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
        Tell us about your teaching credentials
        </Box>
        
      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          (This section requires completion)
        </Typography>
      </Box>

      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
        }}
      >
        <CardContent>
          {qualificationQuestions.map((question, index) => {
            const isUnanswered = qualifications?.[question.key] === undefined || qualifications[question.key] === -1;
            return (
            <React.Fragment key={question.key}>
              <FormControl
                fullWidth
                error={submitted && isUnanswered}
                sx={{
                  mb: index < qualificationQuestions.length - 1 ? 3 : 0,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                  ...(submitted && isUnanswered ? {
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #d32f2f",
                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                  } : {}),
                }}
              >
                <FormLabel
                  id={`${question.key}-label`}
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    flex: "1 1 auto",
                    minWidth: "300px",
                  }}
                >
                  {question.label}
                </FormLabel>
                <RadioGroup
                  row
                  value={
                    qualifications?.[question.key] !== undefined
                      ? String(qualifications[question.key])
                      : "-1"
                  }
                  onChange={(e) =>
                    handleQualificationChange(question.key, e.target.value)
                  }
                  sx={{ flex: "0 0 auto" }}
                >
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label="No"
                  />
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label="Yes"
                  />
                </RadioGroup>
              </FormControl>
              {index < qualificationQuestions.length - 1 && (
                <Divider sx={{ my: 2 }} />
              )}
            </React.Fragment>
          );
          })}

          <Divider sx={{ my: 3 }} />

          <FormControl
            fullWidth
            error={submitted && (qualifications?.["Student"] === undefined || qualifications["Student"] === -1)}
            sx={{
              mb: 3,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              ...(submitted && (qualifications?.["Student"] === undefined || qualifications["Student"] === -1) ? {
                p: 1.5,
                borderRadius: 1,
                border: "1px solid #d32f2f",
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              } : {}),
            }}
          >
            <FormLabel
              id="student-label"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                flex: "1 1 auto",
                minWidth: "300px",
              }}
            >
              Are you currently studying for your A-level or Degree?
            </FormLabel>
            <RadioGroup
              row
              value={
                qualifications?.["Student"] !== undefined
                  ? String(qualifications["Student"])
                  : "-1"
              }
              onChange={(e) => handleStudentQualification(e.target.value)}
              sx={{ flex: "0 0 auto" }}
            >
              <FormControlLabel value="0" control={<Radio />} label="No" />
              <FormControlLabel value="1" control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>

          {qualifications?.["Student"] > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControl
                fullWidth
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <FormLabel
                  id="student-type-label"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    flex: "1 1 auto",
                    minWidth: "300px",
                  }}
                >
                  A-level student or University student
                </FormLabel>
                <RadioGroup
                  row
                  value={
                    qualifications?.["A-level Student"] !== undefined
                      ? String(qualifications["A-level Student"])
                      : "-1"
                  }
                  onChange={(e) =>
                    handleQualificationChange("A-level Student", e.target.value)
                  }
                  sx={{ flex: "0 0 auto" }}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label="A-level"
                  />
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label="University"
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}

          {/* Confirmation Checkbox - appears only when all questions are answered */}
          {allQuestionsAnswered && (
            <>
              <Divider sx={{ my: 4 }} />
              <Box
                sx={{
                  p: 3,
                  backgroundColor: "rgba(113, 45, 148, 0.04)",
                  borderRadius: 2,
                  border: "1px solid rgba(113, 45, 148, 0.2)",
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <Checkbox
                  checked={confirmationChecked}
                  onChange={handleConfirmationChange}
                  sx={{
                    color: "#712d94",
                    "&.Mui-checked": {
                      color: "#712d94",
                    },
                    padding: "9px",
                    flexShrink: 0,
                    alignSelf: "flex-start",
                    "& input": {
                      minWidth: "3em !important",
                      width: "3em !important",
                    },
                  }}
                />
                <Typography
                  component="label"
                  sx={{
                    fontSize: "1rem",
                    color: "#000",
                    mt: "9px",
                    cursor: "default",
                    userSelect: "none",
                  }}
                >
                  I certify that the information I have provided is true and correct, and I agree to the{" "}
                  <MuiLink
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#712d94",
                      textDecoration: "underline",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Terms and Conditions.
                  </MuiLink>
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
